import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Eye,
  DollarSign,
  Calendar,
  User,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import { leadService } from '../../services/leadService';

const CRMKanban = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggingLead, setDraggingLead] = useState(null);
  const [leadsWithProposals, setLeadsWithProposals] = useState({});

  // Definir colunas do funil
  const columns = [
    { id: 'novo', title: 'Novos Leads', color: 'bg-blue-100 border-blue-300' },
    { id: 'em_contato', title: 'Em Contato', color: 'bg-yellow-100 border-yellow-300' },
    { id: 'qualificado', title: 'Qualificado', color: 'bg-purple-100 border-purple-300' },
    { id: 'proposta_enviada', title: 'Proposta Enviada', color: 'bg-orange-100 border-orange-300' },
    { id: 'negociacao', title: 'Negociação', color: 'bg-indigo-100 border-indigo-300' },
    { id: 'ganho', title: 'Ganho', color: 'bg-green-100 border-green-300' },
  ];

  // Carregar leads
  useEffect(() => {
    loadLeads();
  }, []);

  const loadLeads = async () => {
    setLoading(true);
    try {
      // Buscar apenas leads ativos (excluir perdidos e cancelados)
      const data = await leadService.getAll();
      const activeLeads = data.filter(
        lead => lead.status !== 'perdido' && lead.status !== 'cancelado'
      );
      setLeads(activeLeads);
      
      // Buscar propostas para cada lead e calcular valores
      const proposalsMap = {};
      for (const lead of activeLeads) {
        try {
          const proposals = await leadService.getProposals(lead._id);
          // Somar propostas ativas (não rejeitadas/expiradas)
          const activeProposals = proposals.filter(
            p => !['recusada', 'expirada'].includes(p.status)
          );
          const totalValue = activeProposals.reduce((sum, p) => sum + (p.estimated_value || 0), 0);
          proposalsMap[lead._id] = totalValue;
        } catch (error) {
          console.error(`Erro ao buscar propostas do lead ${lead._id}:`, error);
          proposalsMap[lead._id] = 0;
        }
      }
      setLeadsWithProposals(proposalsMap);
    } catch (error) {
      console.error('Erro ao carregar leads:', error);
      toast({
        title: 'Erro ao carregar leads',
        description: 'Não foi possível carregar os leads.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Obter leads por coluna
  const getLeadsByStatus = (status) => {
    return leads.filter(lead => lead.status === status);
  };

  // Calcular valor total por coluna
  const getTotalValue = (status) => {
    const columnLeads = getLeadsByStatus(status);
    // Somar valores das propostas + estimated_value dos leads (fallback)
    const total = columnLeads.reduce((sum, lead) => {
      const proposalValue = leadsWithProposals[lead._id] || 0;
      const leadValue = lead.estimated_value || 0;
      // Usar o maior valor entre propostas e estimated_value do lead
      return sum + Math.max(proposalValue, leadValue);
    }, 0);
    return total;
  };

  // Drag handlers
  const handleDragStart = (e, lead) => {
    setDraggingLead(lead);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggingLead(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStatus) => {
    e.preventDefault();
    
    if (!draggingLead || draggingLead.status === targetStatus) {
      setDraggingLead(null);
      return;
    }

    // Atualizar status do lead
    try {
      await leadService.update(draggingLead._id, { status: targetStatus });
      
      toast({
        title: 'Lead movido!',
        description: `${draggingLead.company} movido para ${leadService.translateStatus(targetStatus)}`,
      });

      // Atualizar lista
      setLeads(leads.map(lead => 
        lead._id === draggingLead._id 
          ? { ...lead, status: targetStatus }
          : lead
      ));
    } catch (error) {
      console.error('Erro ao mover lead:', error);
      toast({
        title: 'Erro ao mover lead',
        description: 'Não foi possível atualizar o status do lead.',
        variant: 'destructive',
      });
    } finally {
      setDraggingLead(null);
    }
  };

  // Verificar se lead precisa atenção
  const needsAttention = (lead) => {
    const daysSince = leadService.daysSinceLastInteraction(lead.last_interaction_date);
    return daysSince === null || daysSince > 7;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Carregando funil...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/portal/leads')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Funil de Vendas</h1>
            <p className="text-gray-600 mt-1">
              Arraste os cards para mover leads entre etapas
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/portal/crm/dashboard')}
          >
            Dashboard
          </Button>
          <Button
            onClick={() => navigate('/portal/leads')}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Ver Lista de Leads
          </Button>
        </div>
      </div>

      {/* Estatísticas Gerais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Total de Leads Ativos</p>
              <p className="text-3xl font-bold text-gray-900">{leads.length}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Valor Total em Pipeline</p>
              <p className="text-2xl font-bold text-green-600">
                {leadService.formatCurrency(
                  leads.reduce((sum, lead) => {
                    const proposalValue = leadsWithProposals[lead._id] || 0;
                    const leadValue = lead.estimated_value || 0;
                    return sum + Math.max(proposalValue, leadValue);
                  }, 0)
                )}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Em Negociação</p>
              <p className="text-3xl font-bold text-indigo-600">
                {getLeadsByStatus('negociacao').length}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Leads Ganhos</p>
              <p className="text-3xl font-bold text-green-600">
                {getLeadsByStatus('ganho').length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {columns.map(column => {
          const columnLeads = getLeadsByStatus(column.id);
          const totalValue = getTotalValue(column.id);

          return (
            <div
              key={column.id}
              className="flex-shrink-0 w-80"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, column.id)}
            >
              {/* Column Header */}
              <Card className={`${column.color} border-2 mb-3`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold">
                      {column.title}
                    </CardTitle>
                    <Badge variant="secondary" className="font-bold">
                      {columnLeads.length}
                    </Badge>
                  </div>
                  {totalValue > 0 && (
                    <div className="text-xs text-gray-600 flex items-center gap-1 mt-2">
                      <DollarSign className="h-3 w-3" />
                      {leadService.formatCurrency(totalValue)}
                    </div>
                  )}
                </CardHeader>
              </Card>

              {/* Column Cards */}
              <div className="space-y-3 min-h-[400px]">
                {columnLeads.length > 0 ? (
                  columnLeads.map(lead => (
                    <Card
                      key={lead._id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, lead)}
                      onDragEnd={handleDragEnd}
                      className={`
                        cursor-move hover:shadow-lg transition-shadow
                        ${draggingLead?._id === lead._id ? 'opacity-50' : ''}
                        ${needsAttention(lead) && column.id !== 'ganho' ? 'border-orange-400 border-2' : ''}
                      `}
                    >
                      <CardContent className="p-4 space-y-3">
                        {/* Alert Badge */}
                        {needsAttention(lead) && column.id !== 'ganho' && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Precisa atenção
                          </Badge>
                        )}

                        {/* Company Name */}
                        <div>
                          <h3 className="font-semibold text-gray-900 text-sm">
                            {lead.company}
                          </h3>
                          <p className="text-xs text-gray-500">
                            {lead.name}
                          </p>
                        </div>

                        {/* Estimated Value */}
                        {lead.estimated_value && (
                          <div className="flex items-center gap-1 text-sm text-green-600 font-medium">
                            <DollarSign className="h-3 w-3" />
                            {leadService.formatCurrency(lead.estimated_value)}
                          </div>
                        )}

                        {/* Probability */}
                        {lead.probability && (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-500 h-2 rounded-full transition-all"
                                style={{ width: `${lead.probability}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-600">{lead.probability}%</span>
                          </div>
                        )}

                        {/* Last Interaction */}
                        {lead.last_interaction_date && (
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Calendar className="h-3 w-3" />
                            Último contato: {leadService.daysSinceLastInteraction(lead.last_interaction_date)} dia(s)
                          </div>
                        )}

                        {/* Action Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Salvar ID no sessionStorage
                            sessionStorage.setItem('openLeadDetail', lead._id);
                            navigate('/portal/leads');
                          }}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Ver Detalhes
                        </Button>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    Nenhum lead nesta etapa
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Instruções */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <AlertCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">Como usar o Funil</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Arraste os cards para mover leads entre etapas do funil</li>
                <li>• Cards com borda laranja precisam de atenção (sem contato há mais de 7 dias)</li>
                <li>• Clique em "Ver Detalhes" para adicionar interações e propostas</li>
                <li>• O valor mostrado em cada coluna é a soma dos valores estimados dos leads</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CRMKanban;
